/**
 * What each plan-only run found.
 *
 * A finished plan's status always says "no changes": the pipeline cannot tell a
 * plan with changes from one without. So the page reads the answer from the
 * plan's log, the same way it reads why a deploy failed: one filtered read for
 * up to 100 runs, only for rows on screen, kept for the browser session.
 */
import type { DeploymentRecord } from '@/services/deploymentService';
import {
  MAX_STREAMS_PER_READ,
  filterLogPage,
  isMissingStreamError,
  resolveDeploymentLogTarget,
} from '@/services/logsService';
import { PLAN_RESULT_FILTER_PATTERN, planOutcome, type PlanOutcome, type ProgressEvent } from './deployProgress';
import { isLogExpired } from './deployStatus';

export interface PlanInfo {
  /** `loading`: the log is being read. `ready`: the answer is in. `unavailable`: the read failed, a refresh tries again. */
  state: 'loading' | 'ready' | 'unavailable';
  outcome?: PlanOutcome;
}

const CACHE_PREFIX = 'deploy-plan:v1:';
const UNKNOWN: PlanInfo = { state: 'ready', outcome: { kind: 'unknown' } };
/** A plan that just ended may not have its last lines searchable yet. Look again before settling for "unknown". */
const SETTLE_MS = 3 * 60 * 1000;
const RECHECK_MS = 20_000;
const MAX_RECHECKS = 4;
const MAX_PAGES = 20;

const cache = new Map<string, PlanInfo>();
const rechecks = new Map<string, number>();
const listeners = new Set<() => void>();
let queue: Promise<void> = Promise.resolve();

function emit() {
  for (const listener of listeners) listener();
}

export function onPlanResultsChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPlanResult(deploymentId: string): PlanInfo | undefined {
  return cache.get(deploymentId);
}

function restore(deploymentId: string): PlanInfo | undefined {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + deploymentId);
    return raw ? (JSON.parse(raw) as PlanInfo) : undefined;
  } catch {
    return undefined;
  }
}

function settle(record: DeploymentRecord, info: PlanInfo) {
  const id = record.deploymentId;
  const ended = Date.parse(record.endedAt || '');
  const justEnded = Number.isFinite(ended) && Date.now() - ended < SETTLE_MS;
  const tries = rechecks.get(id) || 0;
  if (info.outcome?.kind === 'unknown' && justEnded && tries < MAX_RECHECKS) {
    rechecks.set(id, tries + 1);
    cache.set(id, { state: 'loading' });
    setTimeout(() => {
      cache.delete(id);
      requestPlanResults([record]);
    }, RECHECK_MS);
    return;
  }
  cache.set(id, info);
  // "Unknown" is kept in memory only: the next session reads the log once more.
  if (info.state !== 'ready' || info.outcome?.kind === 'unknown') return;
  try {
    sessionStorage.setItem(CACHE_PREFIX + id, JSON.stringify(info));
  } catch {
    // Session storage is full. The answer stays in memory for this page.
  }
}

/**
 * Read one batch. CloudWatch refuses the whole read when one stream does not
 * exist, so a refused batch is read one run at a time.
 */
async function readBatch(group: string, records: DeploymentRecord[]): Promise<void> {
  const streamOf = (record: DeploymentRecord) => resolveDeploymentLogTarget(record).stream!;
  let startTime = Infinity;
  let endTime = 0;
  for (const record of records) {
    const started = Date.parse(record.startedAt || '');
    if (!Number.isFinite(started)) continue;
    const ended = Date.parse(record.endedAt || '');
    startTime = Math.min(startTime, started - 60_000);
    // A plan stops itself at two hours, so a row with no end time ended within three.
    endTime = Math.max(endTime, (Number.isFinite(ended) ? ended : started + 3 * 60 * 60 * 1000) + 5 * 60_000);
  }
  if (!Number.isFinite(startTime)) {
    startTime = 0;
    endTime = Date.now();
  }
  try {
    const byStream = new Map<string, ProgressEvent[]>();
    let token: string | undefined;
    for (let page = 0; page < MAX_PAGES; page++) {
      const res = await filterLogPage({
        group,
        streams: records.map(streamOf),
        pattern: PLAN_RESULT_FILTER_PATTERN,
        startTime,
        endTime,
        nextToken: token,
      });
      for (const event of res.events) {
        if (!event.logStreamName) continue;
        const events = byStream.get(event.logStreamName) || [];
        events.push({ message: event.message, timestamp: event.timestamp });
        byStream.set(event.logStreamName, events);
      }
      token = res.nextToken;
      if (!token) break;
    }
    for (const record of records) {
      settle(record, { state: 'ready', outcome: planOutcome(byStream.get(streamOf(record)) || []) });
    }
  } catch (error) {
    if (isMissingStreamError(error)) {
      if (records.length === 1) {
        settle(records[0], UNKNOWN);
        return;
      }
      for (const record of records) await readBatch(group, [record]);
      return;
    }
    console.warn('Could not read what these plans found', error);
    for (const record of records) cache.set(record.deploymentId, { state: 'unavailable' });
  }
}

/**
 * Make sure an answer exists, or is on its way, for each finished plan given.
 * Safe to call on every render: a plan already known or being read is skipped.
 */
export function requestPlanResults(records: DeploymentRecord[]): void {
  const pending: DeploymentRecord[] = [];
  let changed = false;
  for (const record of records) {
    const id = record.deploymentId;
    if (cache.has(id)) continue;
    changed = true;
    const saved = restore(id);
    if (saved) {
      cache.set(id, saved);
      continue;
    }
    // No task means no log, and a log past its 30 days is gone: either way the answer is not to be had.
    if (!resolveDeploymentLogTarget(record).stream || isLogExpired(record)) {
      cache.set(id, UNKNOWN);
      continue;
    }
    cache.set(id, { state: 'loading' });
    pending.push(record);
  }
  if (changed) emit();
  if (pending.length === 0) return;

  const byGroup = new Map<string, DeploymentRecord[]>();
  for (const record of pending) {
    const { group } = resolveDeploymentLogTarget(record);
    byGroup.set(group, [...(byGroup.get(group) || []), record]);
  }
  for (const [group, groupRecords] of byGroup) {
    for (let i = 0; i < groupRecords.length; i += MAX_STREAMS_PER_READ) {
      const batch = groupRecords.slice(i, i + MAX_STREAMS_PER_READ);
      // One read at a time: the CloudWatch request quota is shared.
      queue = queue.then(() => readBatch(group, batch)).then(emit, emit);
    }
  }
}

/** Forget failed reads, so the next request tries them again. Called by a manual refresh. */
export function retryUnavailablePlanResults(): void {
  for (const [id, info] of cache) {
    if (info.state === 'unavailable') cache.delete(id);
  }
}
